import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Card,
  CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import UserManagement from './UserManagement';
import Invoicing from './Invoicing';
import Quotes from './Quotes';
import QuoteReminderStats from './QuoteReminderStats';
import config from '../../config/config';
import byggnadsvardImage from '../../assets/images/byggnadsvard.jpg';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [stats, setStats] = useState<any>(null);
  const [interactions, setInteractions] = useState<any[]>([]);
  const [uniqueVisitors, setUniqueVisitors] = useState<{ unique_count: number } | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchData();

    // Uppdatera data var 5:e minut
    const interval = setInterval(fetchData, 5 * 60 * 1000);

    // Städa upp intervallet när komponenten unmountas
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Hämta statistik för de senaste 30 dagarna
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const dateStr = thirtyDaysAgo.toISOString().split('T')[0];

      console.log('Hämtar data från:', dateStr);

      const [statsRes, interactionsRes, uniqueRes] = await Promise.all([
        fetch(`${config.api.baseUrl}/analytics/stats?startDate=${dateStr}`, { headers }),
        fetch(`${config.api.baseUrl}/analytics/interactions?startDate=${dateStr}`, { headers }),
        fetch(`${config.api.baseUrl}/analytics/unique-visitors?startDate=${dateStr}`, { headers })
      ]);

      if (!statsRes.ok || !interactionsRes.ok || !uniqueRes.ok) {
        throw new Error('Kunde inte hämta data');
      }

      const statsData = await statsRes.json();
      const interactionsData = await interactionsRes.json();
      const uniqueData = await uniqueRes.json();

      console.log('Mottagna unika besökare:', uniqueData);

      // Formatera data för diagram
      const formattedStats = Object.entries(
        statsData.reduce((acc: any, curr: any) => {
          const date = curr.date;
          if (!acc[date]) acc[date] = { date, visits: 0 };
          acc[date].visits += curr.visit_count;
          return acc;
        }, {})
      ).map(([date, data]: [string, any]) => ({
        date,
        visits: data.visits
      })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      setStats(formattedStats);
      setInteractions(interactionsData);
      setUniqueVisitors(uniqueData);
      
      console.log('Uppdaterat state med unika besökare:', uniqueData.unique_count);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        pt: { xs: 12, sm: 14 },
        pb: { xs: 8, sm: 10 },
        position: 'relative',
        overflowX: 'hidden'
      }}
    >
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)), url(${byggnadsvardImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 0
        }}
      />
      <Container 
        maxWidth={false}
        sx={{ 
          position: 'relative',
          zIndex: 1,
          maxWidth: '100vw',
          width: '100vw',
          px: 0
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1">
            Admin Dashboard
          </Typography>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="Statistik" />
            <Tab label="Användarhantering" />
            <Tab label="Fakturering" />
            <Tab label="Offert" />
            <Tab label="Påminnelse-statistik" />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            {/* Översiktskort */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Totala besök (30 dagar)
                  </Typography>
                  <Typography variant="h3">
                    {stats?.reduce((sum: number, day: any) => sum + day.visits, 0) || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Unika besökare (30 dagar)
                  </Typography>
                  <Typography variant="h3">
                    {uniqueVisitors?.unique_count || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Interaktioner idag
                  </Typography>
                  <Typography variant="h3">
                    {interactions.filter((i: any) => 
                      new Date(i.created_at).toDateString() === new Date().toDateString()
                    ).length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Besöksdiagram */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Besöksstatistik (senaste 30 dagarna)
                </Typography>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => new Date(date).toLocaleDateString('sv-SE')}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(date) => new Date(date).toLocaleDateString('sv-SE')}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="visits" stroke="#8884d8" name="Besök" />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Interaktionstabell */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Senaste interaktioner
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tidpunkt</TableCell>
                        <TableCell>Sida</TableCell>
                        <TableCell>Händelse</TableCell>
                        <TableCell>Detaljer</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {interactions.slice(0, 10).map((interaction, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {new Date(interaction.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell>{interaction.page_path}</TableCell>
                          <TableCell>{interaction.action_type}</TableCell>
                          <TableCell>
                            {interaction.action_details ? JSON.stringify(interaction.action_details) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <UserManagement />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Invoicing />
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Quotes />
        </TabPanel>

        <TabPanel value={currentTab} index={4}>
          <QuoteReminderStats />
        </TabPanel>
      </Container>
    </Box>
  );
};

export default Dashboard; 